import { Tooltip, Typography, Box } from '@mui/material';

import { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocation } from 'react-router-dom';

import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { truncateString } from 'common/utils/utilFunctions';

import { getClusterReviewData } from '../../api';
import { ClusterDetail } from '../../api/type-def';

import styles from './ClusterTable.module.scss';

const ClusterTable = () => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [clusterReviewTableData, setClusterReviewTableData] = useState<
		ClusterDetail[]
	>([]);
	const [totalRecordsCount, setTotalRecordsCount] = useState<number>(0);
	const [totalPagesCount, setTotalPagesCount] = useState(0);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const location = useLocation();
	const {
		state: { clusterName },
	} = location;

	const dispatch = useDispatch();

	const handleClose = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	const fetchClusterReviewTableData = async (data: any) => {
		setIsLoading(true);
		setClusterReviewTableData([]);
		const resp = await getClusterReviewData(data, clusterName);
		if (resp?.responseBody) {
			setClusterReviewTableData(resp?.responseBody.controlGroupDTOs);
			setTotalRecordsCount(resp?.responseBody.totalCount);
			setTotalPagesCount(resp?.responseBody.totalPage);
			setIsLoading(false);
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
				}),
			);
			setIsLoading(false);
		}
	};

	const handlePageChange = (data: any) => {
		fetchClusterReviewTableData(data);
	};

	const handleSortChange = (data: any) => {
		fetchClusterReviewTableData(data);
	};

	const columns: readonly Column[] = [
		{
			id: 'clustersId',
			label: 'Clusters ID',
			isSortable: true,
			disablePadding: false,
			render: (params: ClusterDetail) => (
				<Typography
					component="div"
					data-testid="name-link"
					className={styles['table-row-clickable']}
				>
					{params?.clustersId}
				</Typography>
			),
		},
		{
			id: 'controlId',
			label: 'Control ID',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'action',
			label: 'Action',
			disablePadding: false,
			isSortable: false,
		},
		{
			id: 'comments',
			label: 'Comments',
			disablePadding: false,
			isSortable: false,
		},
		{
			id: 'assessmentUnit',
			label: 'Assessment Unit',
			disablePadding: false,
			isSortable: false,
		},
		{
			id: 'controlDescription',
			label: 'Control Description',
			disablePadding: false,
			isSortable: false,
			render: ({ controlDescription }: ClusterDetail) => (
				<Typography className={styles['control-des']}>
					{controlDescription.length > 15 ? (
						<Tooltip title={controlDescription}>
							<span>{`${truncateString(controlDescription, 300)}...`}</span>
						</Tooltip>
					) : (
						controlDescription
					)}
				</Typography>
			),
		},
	];

	return (
		<Box>
			<TableComponent
				rows={clusterReviewTableData}
				columns={columns}
				totalItems={totalRecordsCount}
				totalPagesCount={totalPagesCount}
				defaultSortKey=""
				onPageHandler={handlePageChange}
				isLoading={isLoading}
				tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
				onSortHandler={handleSortChange}
				enableHeaderColor
				isRenderPagination
			/>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};

export default ClusterTable;
